/**
 * Fast Cortex - Main App Component
 * Hierarchical drill-down navigation for job costing analytics
 */
import React, { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { DateRangeProvider } from './contexts/DateRangeContext'
import { GlobalFilterProvider } from './contexts/GlobalFilterContext'
import { SearchProvider } from './contexts/SearchContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import Login from './pages/Login'
import './App.css'

// Lazy load project components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'))
const ProjectList = lazy(() => import('./components/projects/ProjectList'))
const ProjectDetailNew = lazy(() => import('./components/projects/ProjectDetailNew'))
const Jobs = lazy(() => import('./pages/Jobs'))
const JobDetail = lazy(() => import('./pages/JobDetail'))
const Help = lazy(() => import('./pages/Help'))
const OrderBook = lazy(() => import('./pages/OrderBook'))
const PlanVsActual = lazy(() => import('./pages/PlanVsActual'))
const LabourAnalysis = lazy(() => import('./pages/LabourAnalysis'))
const StockAge = lazy(() => import('./pages/StockAge'))
const WIP = lazy(() => import('./pages/WIP'))
const StockAgePolicy = lazy(() => import('./pages/StockAge/PolicySettings'))

// Import Layout
import Layout from './components/Layout'
import { ThemeProvider } from './contexts/ThemeContext'

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  </div>
)

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <DateRangeProvider>
            <GlobalFilterProvider>
              <SearchProvider>
                <ThemeProvider>
                  <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <Dashboard />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/*"
                    element={<Navigate to="/" replace />}
                  />
                  <Route
                    path="/jobs"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <Jobs />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/jobs/:jobId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <JobDetail />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <ProjectList />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/projects/:projectId"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <ProjectDetailNew />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/order-book"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <OrderBook />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/plan-vs-actual"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <PlanVsActual />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/labour-analysis"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <LabourAnalysis />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock-age"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <StockAge />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/wip"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <WIP />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock-age/policy"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <ErrorBoundary>
                            <StockAgePolicy />
                          </ErrorBoundary>
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/help/*"
                    element={
                      <ProtectedRoute>
                        <ErrorBoundary>
                          <Help />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </ThemeProvider>
          </SearchProvider>
          </GlobalFilterProvider>
          </DateRangeProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
